/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import axios from 'axios';

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  faviconUrl?: string;
  companyName?: string;
}

interface TenantContextType {
  tenant: TenantInfo | null;
  tenantSlug: string | null;
  isLoading: boolean;
  error: string | null;
  setTenantFromDiscovery: (info: DiscoveredTenant) => void;
  clearTenant: () => void;
}

export interface DiscoveredTenant {
  tenantSlug: string;
  tenantName: string;
  logoUrl?: string;
  primaryColor?: string;
}

interface DomainResolutionResult {
  tenantSlug: string;
  tenantName: string;
  companyName?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  faviconUrl?: string;
  domainType: string;
}

interface PortalBrandingResult {
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  companyName?: string;
  tenantName: string;
}

function setDynamicFavicon(faviconUrl?: string | null) {
  if (!faviconUrl) return;
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const RESERVED_SUBDOMAINS = ['www', 'localhost', 'app', 'api', 'portal', 'admin'];
const KNOWN_DOMAINS = ['destekgor.com', 'localhost'];

function isKnownDomain(hostname: string): boolean {
  return KNOWN_DOMAINS.some(d => hostname === d || hostname.endsWith(`.${d}`));
}

function getTenantSlugFromUrl(): string | null {
  const hostname = window.location.hostname;

  // Only extract subdomain from known domains
  if (isKnownDomain(hostname)) {
    const parts = hostname.split('.');
    if (parts.length >= 2 && !RESERVED_SUBDOMAINS.includes(parts[0])) {
      return parts[0];
    }
  }

  // Query parameter (for development)
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  if (tenantParam) {
    return tenantParam;
  }

  // localStorage (persisted from previous session). Guard against reserved subdomains
  // — older builds wrote the host's first label here unconditionally, which left invalid
  // values like "app" in browsers and made every authenticated request 404 with
  // TENANT_NOT_FOUND. Strip the bad value so the fallback below kicks in.
  const storedSlug = localStorage.getItem('tenantSlug');
  if (storedSlug && !RESERVED_SUBDOMAINS.includes(storedSlug)) {
    return storedSlug;
  }
  if (storedSlug) {
    localStorage.removeItem('tenantSlug');
  }

  // If on a known domain without subdomain, default to demo
  if (isKnownDomain(hostname)) {
    return 'demo';
  }

  // Custom domain — will be resolved async
  return null;
}

function getInitialTenantInfo(slug: string | null): TenantInfo | null {
  if (!slug) return null;
  return {
    id: '',
    name: slug.charAt(0).toUpperCase() + slug.slice(1),
    slug: slug,
  };
}

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const initialSlug = getTenantSlugFromUrl();
  const [tenant, setTenant] = useState<TenantInfo | null>(() => getInitialTenantInfo(initialSlug));
  const [tenantSlug, setTenantSlug] = useState<string | null>(initialSlug);
  const [isLoading, setIsLoading] = useState(!initialSlug);
  const [error, setError] = useState<string | null>(null);

  // Resolve custom domain if slug not determined from URL
  useEffect(() => {
    if (initialSlug) return; // Already resolved

    const hostname = window.location.hostname;
    if (isKnownDomain(hostname)) return;

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
    const resolveUrl = `${API_URL}/api/resolve-domain?domain=${encodeURIComponent(hostname)}`;

    axios.get<DomainResolutionResult>(resolveUrl)
      .then((response) => {
        const data = response.data;
        setTenantSlug(data.tenantSlug);
        setTenant({
          id: '',
          name: data.tenantName,
          slug: data.tenantSlug,
          logoUrl: data.logoUrl,
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          faviconUrl: data.faviconUrl,
          companyName: data.companyName,
        });
        localStorage.setItem('tenantSlug', data.tenantSlug);
        setDynamicFavicon(data.faviconUrl);
      })
      .catch(() => {
        setError('Domain çözümlenemedi');
        // Fallback to demo
        setTenantSlug('demo');
        setTenant(getInitialTenantInfo('demo'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [initialSlug]);

  // Fetch branding for subdomain-resolved slugs (e.g., newton.destekgor.com)
  useEffect(() => {
    if (!initialSlug) return; // Custom domain — handled above
    // Only fetch branding for subdomains of known domains (not default like demo from localStorage)
    const hostname = window.location.hostname;
    if (!isKnownDomain(hostname)) return;
    // Check if this is a subdomain (not the base domain itself)
    const parts = hostname.split('.');
    const isSubdomain = parts.length >= 2 && !RESERVED_SUBDOMAINS.includes(parts[0]) && hostname !== 'localhost';
    if (!isSubdomain) return;

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
    axios.get<PortalBrandingResult>(`${API_URL}/api/branding/by-slug/${encodeURIComponent(initialSlug)}`)
      .then((response) => {
        const data = response.data;
        setTenant({
          id: '',
          name: data.tenantName,
          slug: initialSlug,
          logoUrl: data.logoUrl,
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          faviconUrl: data.faviconUrl,
          companyName: data.companyName,
        });
        setDynamicFavicon(data.faviconUrl);
      })
      .catch(() => {
        // Branding fetch failed — keep the basic slug-based info
      });
  }, [initialSlug]);

  useEffect(() => {
    if (tenantSlug) {
      localStorage.setItem('tenantSlug', tenantSlug);
    }
  }, [tenantSlug]);

  const setTenantFromDiscovery = (info: DiscoveredTenant) => {
    setTenantSlug(info.tenantSlug);
    setTenant({
      id: '',
      name: info.tenantName,
      slug: info.tenantSlug,
      logoUrl: info.logoUrl,
      primaryColor: info.primaryColor,
    });
    localStorage.setItem('tenantSlug', info.tenantSlug);
  };

  const clearTenant = () => {
    setTenantSlug(null);
    setTenant(null);
    localStorage.removeItem('tenantSlug');
  };

  return (
    <TenantContext.Provider value={{ tenant, tenantSlug, isLoading, error, setTenantFromDiscovery, clearTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
